/**
 * PromoPopup - 멀티 슬라이드 프로모션 팝업
 * - 여러 장의 이미지를 캐러셀로 표시
 * - 좌우 화살표 + 페이지네이션
 * - 각 슬라이드 클릭 시 linkUrl 이동
 * - "오늘 하루 보지 않기" + "닫기"
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';
import {
  useActivePopup,
  isPopupDismissedToday,
  setPopupDismissedToday,
} from '../hooks/useActivePopup';
import type { PopupDoc } from '../hooks/useActivePopup';

interface PromoPopupProps {
  forceOpen?: boolean;
  onClose?: () => void;
  isPreviewMode?: boolean;
  previewPopups?: PopupDoc[];
}

const PromoPopup: React.FC<PromoPopupProps> = ({ forceOpen, onClose, isPreviewMode = false, previewPopups }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { popups: livePopups, loading, shouldShowPopup } = useActivePopup();
  const popups = previewPopups || livePopups;

  const [open, setOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [dontShowToday, setDontShowToday] = useState(false);

  // 팝업 표시 조건
  useEffect(() => {
    if (forceOpen !== undefined) { setOpen(forceOpen); return; }
    if (loading) return;
    if (previewPopups) { setOpen(previewPopups.length > 0); return; }
    if (!shouldShowPopup) { setOpen(false); return; }
    if (!isPreviewMode && isPopupDismissedToday()) { setOpen(false); return; }
    setOpen(true);
  }, [loading, shouldShowPopup, forceOpen, isPreviewMode, previewPopups]);

  const handleClose = useCallback(() => {
    if (dontShowToday && !isPreviewMode) {
      setPopupDismissedToday();
    }
    setOpen(false);
    setCurrentSlide(0);
    onClose?.();
  }, [dontShowToday, onClose, isPreviewMode]);

  const handlePrev = () => setCurrentSlide((prev) => (prev - 1 + popups.length) % popups.length);
  const handleNext = () => setCurrentSlide((prev) => (prev + 1) % popups.length);

  const handleImageClick = useCallback(() => {
    const slide = popups[currentSlide];
    if (slide?.linkUrl) {
      window.open(slide.linkUrl, slide.linkTarget || '_blank', 'noopener,noreferrer');
    }
  }, [popups, currentSlide]);

  if (!previewPopups && (loading || popups.length === 0)) return null;
  if (previewPopups && previewPopups.length === 0) return null;

  const currentPopup = popups[currentSlide];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={300}
      PaperProps={{
        sx: {
          borderRadius: { xs: '16px', md: '20px' },
          overflow: 'visible',
          maxWidth: { xs: '92vw', sm: '480px', md: '520px' },
          maxHeight: { xs: '85vh', md: '90vh' },
          margin: { xs: 2, md: 3 },
          background: 'transparent',
          boxShadow: '0 25px 80px rgba(0,0,0,0.35)',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(6px)',
          },
        },
      }}
    >
      <Box sx={{
        position: 'relative',
        borderRadius: { xs: '16px', md: '20px' },
        overflow: 'hidden',
        background: '#fff',
      }}>
        {/* 이미지 캐러셀 영역 */}
        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              transform: `translateX(-${currentSlide * 100}%)`,
              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {popups.map((popup) => (
              <Box
                key={popup.id}
                sx={{
                  width: '100%',
                  flexShrink: 0,
                  cursor: popup.linkUrl ? 'pointer' : 'default',
                }}
                onClick={popup.linkUrl ? handleImageClick : undefined}
              >
                <Box
                  component="img"
                  src={popup.downloadUrl}
                  alt={popup.title || '프로모션'}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: { xs: '55vh', md: '65vh' },
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* 좌우 화살표 (2장 이상일 때) */}
          {popups.length > 1 && (
            <>
              <IconButton
                onClick={handlePrev}
                sx={{
                  position: 'absolute',
                  left: 8, top: '50%', transform: 'translateY(-50%)',
                  zIndex: 5,
                  width: 36, height: 36,
                  bgcolor: 'rgba(0,0,0,0.3)',
                  color: '#fff',
                  backdropFilter: 'blur(4px)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                }}
              >
                <KeyboardArrowLeft sx={{ fontSize: 22 }} />
              </IconButton>
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 8, top: '50%', transform: 'translateY(-50%)',
                  zIndex: 5,
                  width: 36, height: 36,
                  bgcolor: 'rgba(0,0,0,0.3)',
                  color: '#fff',
                  backdropFilter: 'blur(4px)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                }}
              >
                <KeyboardArrowRight sx={{ fontSize: 22 }} />
              </IconButton>
            </>
          )}

          {/* 페이지네이션 (우측 하단) */}
          {popups.length > 1 && (
            <Box sx={{
              position: 'absolute',
              bottom: 10, right: 12,
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1.5, py: 0.5,
              bgcolor: 'rgba(0,0,0,0.4)',
              borderRadius: 50,
              backdropFilter: 'blur(4px)',
            }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#fff', fontWeight: 600 }}>
                {currentSlide + 1} / {popups.length}
              </Typography>
            </Box>
          )}

          {/* 도트 인디케이터 (하단 중앙) */}
          {popups.length > 1 && (
            <Box sx={{
              position: 'absolute',
              bottom: 10, left: '50%', transform: 'translateX(-50%)',
              display: 'flex', gap: 0.6,
            }}>
              {popups.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  sx={{
                    width: currentSlide === i ? 16 : 6,
                    height: 6,
                    borderRadius: 3,
                    bgcolor: currentSlide === i ? '#fff' : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* 하단: "오늘 하루 보지 않기" + "닫기" */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5, py: 1.5,
          borderTop: '1px solid #f0f0f0',
        }}>
          <Typography
            onClick={() => setDontShowToday(!dontShowToday)}
            sx={{
              fontSize: '0.8rem',
              color: dontShowToday ? '#3b82f6' : '#94a3b8',
              fontWeight: dontShowToday ? 600 : 400,
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'color 0.2s',
              '&:hover': { color: '#64748b' },
            }}
          >
            {dontShowToday ? '✓ ' : ''}오늘 하루 보지 않기
          </Typography>
          <Typography
            onClick={handleClose}
            sx={{
              fontSize: '0.8rem',
              color: '#64748b',
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { color: '#334155' },
            }}
          >
            닫기
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default PromoPopup;
